import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { catchError, of } from 'rxjs';
import {
  SciChartSurface,
  NumericAxis,
  FastCandlestickRenderableSeries,
  FastColumnRenderableSeries,
  FastLineRenderableSeries,
  OhlcDataSeries,
  XyDataSeries,
  NumberRange,
  EAutoRange,
  ZoomExtentsModifier,
  ZoomPanModifier,
  MouseWheelZoomModifier,
  CursorModifier,
  XAxisDragModifier,
  YAxisDragModifier,
  StackedColumnCollection,
  StackedColumnRenderableSeries,
  TextLabelProvider,
  LegendModifier
} from 'scichart';
import { PredictAnalysisService } from 'src/app/services/predict-analysis.service';
import { SettingsService } from 'src/app/services/settings.service';
import { WidgetService } from 'src/app/services/widget.service';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';

@Component({
    selector: 'app-playground-detail-widget',
    templateUrl: './playground-detail-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './playground-detail-widget.component.scss'],
    standalone: false
})
export class PlaygroundDetailWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  @Input() vaultName: string;
  @Input() sampleIndex: number;
  @Input() sampleData: any;

  public title = 'Playground Detail';
  public loading = false;
  public error: string = null;

  // Prediction state
  public invokeLoading = false;
  public invokeResult: number[] = null;
  public groundTruth: number = null;
  public sampleCorrect: boolean = null;

  // Feature editor state
  public invokeFeatures: { name: string; shape: number[]; values: any }[] = [];
  public invokeInputs: { [key: string]: any } = {};
  private initialInputs: { [key: string]: any } = {};
  public invokeMeta: { [key: string]: any } = {};
  public selectedFeature: string = null;
  public featureView: 'TABLE' | 'CHART' = 'TABLE';
  public chartId: string;
  public impactChartId: string;
  private chartSurface: SciChartSurface | null = null;
  private impactChartSurface: SciChartSurface | null = null;

  // Impact analysis state
  public impactLoading = false;
  public impactData: any = null;
  public playgroundTableColumns: string[] = [];
  public playgroundTableDS = new MatTableDataSource<any>([]);
  public editingCell: { row: number; col: string } = null;
  public pageSize = 10;
  public pageSizes = [5, 10, 20, 30];
  @ViewChild('detailPaginator') detailPaginator: MatPaginator;

  constructor(
    private el: ElementRef,
    private predictAnalysis: PredictAnalysisService,
    private settingsService: SettingsService,
    private widgetService: WidgetService
  ) {
    super();
    this.chartId = 'playground-chart-' + Math.random().toString(36).slice(2, 10);
    this.impactChartId = 'impact-chart-' + Math.random().toString(36).slice(2, 10);
  }

  ngOnInit(): void {
    this._subs.add = this.widgetService.resize$.subscribe(() => {
      setTimeout(() => this.optimizePageSize(), 100);
    });

    if (this.vaultName && this.sampleIndex !== undefined) {
      this.title = `Sample #${this.sampleIndex}`;
      if (this.sampleData?._isNew) {
        // Blank row: load model info to get feature shapes, fill with zeros
        this.loadModelInfo();
      } else if (this.sampleData?._features) {
        // Imported CSV data: load model info to get feature shapes, then map imported values
        this.loadModelInfoWithImportedData();
      } else {
        // Test data row: load features from API
        this.loadSample();
      }
    }
  }

  private loadModelInfo(): void {
    this.loading = true;
    this.error = null;

    const sub = this.predictAnalysis.getModelInfo(this.teamName, this.vaultName).pipe(
      catchError((err) => {
        this.error = err?.error?.message || err?.message || 'Failed to load model info';
        this.loading = false;
        return of(null);
      })
    ).subscribe((response: any) => {
      sub.unsubscribe();
      this.loading = false;
      if (!response) return;

      const data = typeof response === 'string' ? JSON.parse(response) : response;
      this.invokeMeta = data.meta || {};
      this.invokeFeatures = (data.features || []).map(f => ({
        name: f.name,
        shape: f.shape,
        values: this.createZeros(f.shape)
      }));
      this.invokeInputs = {};
      for (const f of this.invokeFeatures) {
        this.invokeInputs[f.name] = f.values;
      }
      this.initialInputs = JSON.parse(JSON.stringify(this.invokeInputs));

      if (this.invokeFeatures.length > 0) {
        this.selectFeature(this.invokeFeatures[0].name);
      }

      // Pre-load impact analysis in background
      this.runImpactAnalysis(true);
    });
  }

  private loadModelInfoWithImportedData(): void {
    // HDF5 import: _features is already in {name: {shape, values}} format
    this.loading = false;
    const importedFeatures = this.sampleData._features;

    // Build invokeFeatures from imported data
    this.invokeFeatures = Object.entries(importedFeatures).map(([name, fdata]: [string, any]) => ({
      name,
      shape: fdata.shape,
      values: fdata.values
    }));

    this.invokeInputs = {};
    for (const f of this.invokeFeatures) {
      this.invokeInputs[f.name] = f.values;
    }
    this.initialInputs = JSON.parse(JSON.stringify(this.invokeInputs));

    // Use prediction/ground_truth from imported row if available
    if (this.sampleData.prediction !== undefined && this.sampleData.prediction !== null) {
      this.invokeResult = [this.sampleData.prediction];
    }
    if (this.sampleData.ground_truth !== undefined && this.sampleData.ground_truth !== null) {
      this.groundTruth = this.sampleData.ground_truth;
    }
    if (this.invokeResult && this.groundTruth !== null) {
      this.sampleCorrect = (this.invokeResult[0] >= 0.5) === (this.groundTruth >= 0.5);
    }

    if (this.invokeFeatures.length > 0) {
      this.selectFeature(this.invokeFeatures[0].name);
    }

    // Pre-load impact analysis in background
    this.runImpactAnalysis(true);
  }

  private loadSample(): void {
    this.loading = true;
    this.error = null;

    const sub = this.predictAnalysis.inspectSample(this.teamName, this.vaultName, this.sampleIndex).pipe(
      catchError((err) => {
        this.error = err?.error?.message || err?.message || 'Failed to inspect sample';
        this.loading = false;
        return of(null);
      })
    ).subscribe((response: any) => {
      sub.unsubscribe();
      this.loading = false;
      if (!response) return;

      this.invokeResult = Array.isArray(response.prediction) ? response.prediction : [response.prediction];
      this.groundTruth = response.ground_truth;
      // Compute correctness client-side: (pred >= 0.5) === (truth >= 0.5)
      if (this.invokeResult.length > 0 && this.groundTruth !== null) {
        this.sampleCorrect = (this.invokeResult[0] >= 0.5) === (this.groundTruth >= 0.5);
      }

      this.invokeMeta = response.meta || {};
      this.invokeInputs = {};
      this.invokeFeatures = [];
      for (const [fname, fdata] of Object.entries(response.features) as [string, any][]) {
        this.invokeFeatures.push({
          name: fname,
          shape: fdata.shape,
          values: fdata.values
        });
        this.invokeInputs[fname] = fdata.values;
      }
      this.initialInputs = JSON.parse(JSON.stringify(this.invokeInputs));

      if (this.invokeFeatures.length > 0) {
        this.selectFeature(this.invokeFeatures[0].name);
      }

      // Pre-load impact analysis in background
      this.runImpactAnalysis(true);
    });
  }

  invokeModel(): void {
    if (!this.vaultName) return;
    this.invokeLoading = true;
    this.error = null;

    const inputs = Object.keys(this.invokeInputs).length > 0 ? this.invokeInputs : null;

    const sub = this.predictAnalysis.invokeModel(this.teamName, this.vaultName, inputs).pipe(
      catchError((err) => {
        this.error = err?.error?.message || err?.message || 'Failed to invoke model';
        this.invokeLoading = false;
        return of(null);
      })
    ).subscribe((response: any) => {
      sub.unsubscribe();
      this.invokeLoading = false;
      if (!response) return;

      try {
        const data = typeof response === 'string' ? JSON.parse(response) : response;
        this.invokeResult = Array.isArray(data.prediction) ? data.prediction : [data.prediction];
        // Compute correctness: (pred >= 0.5) === (truth >= 0.5)
        if (this.groundTruth !== null && this.invokeResult.length > 0) {
          this.sampleCorrect = (this.invokeResult[0] >= 0.5) === (this.groundTruth >= 0.5);
        }
        if (data.features) {
          this.invokeFeatures = data.features;
          this.invokeInputs = {};
          for (const f of this.invokeFeatures) {
            this.invokeInputs[f.name] = f.values;
          }
        }
        // Emit update to list view
        this.emitSampleUpdate();
        // Re-run impact analysis with updated inputs
        this.runImpactAnalysis();
      } catch (e) {
        this.error = 'Failed to parse response';
      }
    });
  }

  private emitSampleUpdate(): void {
    const prediction = this.invokeResult?.[0] ?? null;
    const error = (prediction !== null && this.groundTruth !== null)
      ? Math.abs(prediction - this.groundTruth)
      : null;
    this.widgetService.playgroundSampleUpdate$.next({
      teamName: this.teamName,
      modelName: this.vaultName,
      sampleIndex: this.sampleIndex,
      prediction,
      ground_truth: this.groundTruth,
      error,
      correct: this.sampleCorrect
    });
  }

  onGroundTruthChange(event: any): void {
    const val = parseFloat(event.target.value);
    this.groundTruth = isNaN(val) ? null : val;
    // Recalculate correctness if we have a prediction
    if (this.invokeResult !== null && this.invokeResult.length > 0 && this.groundTruth !== null) {
      this.sampleCorrect = (this.invokeResult[0] >= 0.5) === (this.groundTruth >= 0.5);
    } else {
      this.sampleCorrect = null;
    }
    this.emitSampleUpdate();
  }

  selectFeature(featureName: string): void {
    this.selectedFeature = featureName;
    this.editingCell = null;
    const featureData = this.invokeInputs[featureName];
    if (!featureData) return;

    const meta = this.invokeMeta[featureName];
    const is2D = Array.isArray(featureData[0]);
    const numColumns = is2D ? featureData[0].length : featureData.length;

    if (meta?.returns?.length === numColumns) {
      this.playgroundTableColumns = ['#', ...meta.returns];
    } else {
      this.playgroundTableColumns = ['#', ...new Array(numColumns).fill(0).map((_e, i) => (i + 1).toString())];
    }

    let tableData: any[];
    if (is2D) {
      tableData = featureData.map((row, rowIdx) => ({
        '#': rowIdx + 1,
        ...Object.fromEntries(row.map((val, i) => [this.playgroundTableColumns[i + 1], val]))
      }));
    } else {
      tableData = [{
        '#': 1,
        ...Object.fromEntries(featureData.map((val, i) => [this.playgroundTableColumns[i + 1], val]))
      }];
    }

    this.playgroundTableDS.data = tableData;
    setTimeout(() => {
      if (this.detailPaginator) {
        this.playgroundTableDS.paginator = this.detailPaginator;
        this.optimizePageSize();
      }
    });

    // Render chart if in chart view
    if (this.featureView === 'CHART') {
      this.renderChart(featureData, is2D);
    }
  }

  onViewChange(view: 'TABLE' | 'CHART'): void {
    this.featureView = view;
    if (view === 'CHART' && this.selectedFeature) {
      const featureData = this.invokeInputs[this.selectedFeature];
      if (featureData) {
        const is2D = Array.isArray(featureData[0]);
        setTimeout(() => this.renderChart(featureData, is2D));
      }
    }
  }

  onTabChange(event: any): void {
    if (event.index === 1) {
      if (!this.impactData && !this.impactLoading) {
        this.runImpactAnalysis();
      } else if (this.impactData && !this.impactChartSurface) {
        setTimeout(() => this.renderImpactChart());
      }
    }
  }

  private async renderChart(featureData: any, is2D: boolean): Promise<void> {
    // Clean up previous chart
    if (this.chartSurface) {
      this.chartSurface.delete();
      this.chartSurface = null;
    }

    // Wait for DOM to be ready
    await new Promise(resolve => setTimeout(resolve, 50));

    const el = document.getElementById(this.chartId);
    if (!el) return;

    const columns = this.playgroundTableColumns.filter(c => c !== '#');

    // Detect OHLCV: 2D data with columns containing open, high, low, close
    const lowerColumns = columns.map(c => c.toLowerCase());
    const hasOHLC = ['open', 'high', 'low', 'close'].every(
      name => lowerColumns.includes(name)
    );

    try {
      const theme = this.settingsService.getChartThemeProvider();
      const { sciChartSurface, wasmContext } = await SciChartSurface.create(this.chartId, {
        theme,
        disableAspect: true
      });
      this.chartSurface = sciChartSurface;

      if (is2D && hasOHLC) {
        // Candlestick chart for OHLCV data
        const oIdx = lowerColumns.indexOf('open');
        const hIdx = lowerColumns.indexOf('high');
        const lIdx = lowerColumns.indexOf('low');
        const cIdx = lowerColumns.indexOf('close');
        const vIdx = lowerColumns.indexOf('volume');

        const xValues = featureData.map((_, i) => i);
        const openValues = featureData.map(row => row[oIdx]);
        const highValues = featureData.map(row => row[hIdx]);
        const lowValues = featureData.map(row => row[lIdx]);
        const closeValues = featureData.map(row => row[cIdx]);

        sciChartSurface.xAxes.add(new NumericAxis(wasmContext, {
          growBy: new NumberRange(0.05, 0.05),
          labelPrecision: 0,
          autoRange: EAutoRange.Always
        }));

        sciChartSurface.yAxes.add(new NumericAxis(wasmContext, {
          growBy: new NumberRange(0.1, 0.1),
          autoRange: EAutoRange.Always,
          labelPrecision: 4
        }));

        const ohlcData = new OhlcDataSeries(wasmContext, {
          xValues, openValues, highValues, lowValues, closeValues
        });

        sciChartSurface.renderableSeries.add(new FastCandlestickRenderableSeries(wasmContext, {
          dataSeries: ohlcData,
          strokeThickness: 1,
          dataPointWidth: 0.7,
          brushUp: '#33ff3377',
          brushDown: '#ff333377',
          strokeUp: '#77ff77',
          strokeDown: '#ff7777'
        }));

        // Add volume as column series on a hidden secondary y-axis
        if (vIdx >= 0) {
          const volumeValues = featureData.map(row => row[vIdx]);
          sciChartSurface.yAxes.add(new NumericAxis(wasmContext, {
            id: 'volume',
            growBy: new NumberRange(0, 4),
            isVisible: false,
            autoRange: EAutoRange.Always
          }));
          sciChartSurface.renderableSeries.add(new FastColumnRenderableSeries(wasmContext, {
            dataSeries: new XyDataSeries(wasmContext, { xValues, yValues: volumeValues }),
            strokeThickness: 0,
            yAxisId: 'volume',
            dataPointWidth: 0.7,
            opacity: 0.4
          }));
        }
      } else if (is2D && columns.length === 1) {
        // Single column, multiple rows → line chart
        const yValues = featureData.map(row => row[0]);
        const xValues = yValues.map((_, i) => i);

        sciChartSurface.xAxes.add(new NumericAxis(wasmContext, {
          growBy: new NumberRange(0.02, 0.02),
          labelPrecision: 0,
          autoRange: EAutoRange.Always
        }));

        sciChartSurface.yAxes.add(new NumericAxis(wasmContext, {
          growBy: new NumberRange(0.1, 0.1),
          autoRange: EAutoRange.Always,
          labelPrecision: 4
        }));

        sciChartSurface.renderableSeries.add(new FastLineRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, { xValues, yValues }),
          strokeThickness: 2,
          stroke: '#4fc3f7'
        }));
      } else {
        // Fallback: 1D data or multi-column non-OHLCV → line chart over columns
        const yValues = is2D ? featureData.map(row => row[0]) : featureData.slice();
        const xValues = yValues.map((_, i) => i);

        sciChartSurface.xAxes.add(new NumericAxis(wasmContext, {
          growBy: new NumberRange(0.02, 0.02),
          labelPrecision: 0,
          autoRange: EAutoRange.Always
        }));

        sciChartSurface.yAxes.add(new NumericAxis(wasmContext, {
          growBy: new NumberRange(0.1, 0.1),
          autoRange: EAutoRange.Always,
          labelPrecision: 4
        }));

        sciChartSurface.renderableSeries.add(new FastLineRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, { xValues, yValues }),
          strokeThickness: 2,
          stroke: '#4fc3f7'
        }));
      }

      // Add interaction modifiers (matches legacy insights-line-chart pattern)
      sciChartSurface.chartModifiers.add(
        new MouseWheelZoomModifier(),
        new ZoomPanModifier(),
        new ZoomExtentsModifier(),
        new XAxisDragModifier(),
        new YAxisDragModifier(),
        new CursorModifier({ showTooltip: true, showAxisLabels: true })
      );
    } catch (error) {
      console.error('Failed to render SciChart:', error);
    }
  }

  runImpactAnalysis(background = false): void {
    if (!this.vaultName || Object.keys(this.invokeInputs).length === 0) return;
    if (!background) {
      this.impactLoading = true;
      this.impactData = null;
      if (this.impactChartSurface) {
        this.impactChartSurface.delete();
        this.impactChartSurface = null;
      }
    }

    const sub = this.predictAnalysis.getImpactFromInputs(this.teamName, this.vaultName, this.invokeInputs).pipe(
      catchError((err) => {
        if (!background) {
          this.error = err?.error?.message || err?.message || 'Failed to run impact analysis';
        }
        this.impactLoading = false;
        return of(null);
      })
    ).subscribe((response: any) => {
      sub.unsubscribe();
      this.impactLoading = false;
      if (!response) return;

      this.impactData = response;
      setTimeout(() => this.renderImpactChart());
    });
  }

  optimizePageSize(): void {
    const table = this.el.nativeElement.querySelector('.detail-table-wrapper');
    const thead = this.el.nativeElement.querySelector('.detail-table-wrapper thead');

    if (table && thead && this.detailPaginator) {
      const tableHeight = table.offsetHeight;
      const theadHeight = thead.offsetHeight;
      const rowHeight = 24;
      const paginatorHeight = 56;

      this.pageSize = Math.max(1, Math.floor((tableHeight - theadHeight - paginatorHeight - 20) / rowHeight));
      this.detailPaginator._changePageSize(this.pageSize);
      this.pageSizes = [
        this.pageSize,
        Math.max(this.pageSize, 10),
        Math.max(this.pageSize, 20),
        Math.max(this.pageSize, 30)
      ].filter((v, i, a) => a.indexOf(v) === i);
    } else {
      setTimeout(() => this.optimizePageSize(), 500);
    }
  }

  private async renderImpactChart(): Promise<void> {
    if (this.impactChartSurface) {
      this.impactChartSurface.delete();
      this.impactChartSurface = null;
    }

    await new Promise(resolve => setTimeout(resolve, 50));

    const el = document.getElementById(this.impactChartId);
    if (!el || !this.impactData) return;

    try {
      const theme = this.settingsService.getChartThemeProvider();
      const { sciChartSurface, wasmContext } = await SciChartSurface.create(this.impactChartId, {
        theme,
        disableAspect: true
      });
      this.impactChartSurface = sciChartSurface;

      const labels = this.impactData.independence.map(x =>
        (x[0] + ' ' + x[1]).replace('no_drop', 'nodrop').replaceAll('_', ' ')
      );

      const labelProvider = new TextLabelProvider({
        labels,
        maxLength: 7
      });

      sciChartSurface.xAxes.add(new NumericAxis(wasmContext, {
        labelProvider,
        autoTicks: false,
        majorDelta: 1,
        minorDelta: 1,
        labelStyle: { fontSize: 9 },
        rotation: 45
      }));
      sciChartSurface.yAxes.add(new NumericAxis(wasmContext, {
        autoRange: EAutoRange.Always
      }));

      const xValues = [...new Array(this.impactData.independence.length).keys()];
      const yValues1 = this.impactData.independence.map(x => Math.abs(x[2]));
      const yValues2 = this.impactData.saliency.map(x => Math.abs(x[2]));

      const stackedCollection = new StackedColumnCollection(wasmContext);

      stackedCollection.add(new StackedColumnRenderableSeries(wasmContext, {
        dataSeries: new XyDataSeries(wasmContext, { xValues, yValues: yValues1, dataSeriesName: 'Independence' }),
        fill: '#8BC34A',
        stroke: '#E4F5FC',
        strokeThickness: 1,
        opacity: 0.8,
        stackedGroupId: 'StackedGroupId-First'
      }));

      stackedCollection.add(new StackedColumnRenderableSeries(wasmContext, {
        dataSeries: new XyDataSeries(wasmContext, { xValues, yValues: yValues2, dataSeriesName: 'Saliency' }),
        fill: '#FFAB40',
        stroke: '#E4F5FC',
        strokeThickness: 1,
        opacity: 0.8,
        stackedGroupId: 'StackedGroupId-Second'
      }));

      sciChartSurface.chartModifiers.add(
        new ZoomExtentsModifier(),
        new ZoomPanModifier(),
        new MouseWheelZoomModifier(),
        new LegendModifier({
          showCheckboxes: false,
          showSeriesMarkers: true,
          showLegend: true
        })
      );

      sciChartSurface.renderableSeries.add(stackedCollection);
    } catch (error) {
      console.error('Failed to render impact chart:', error);
    }
  }

  ngOnDestroy(): void {
    if (this.chartSurface) {
      this.chartSurface.delete();
      this.chartSurface = null;
    }
    if (this.impactChartSurface) {
      this.impactChartSurface.delete();
      this.impactChartSurface = null;
    }
  }

  startEdit(element: any, col: string): void {
    if (col === '#') return;
    this.editingCell = { row: element['#'], col };
    setTimeout(() => {
      const input = document.querySelector('.cell-input') as HTMLInputElement;
      if (input) { input.focus(); input.select(); }
    });
  }

  commitEdit(element: any, col: string, event: any): void {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      element[col] = value;
      const rowIndex = element['#'] - 1;
      const colIndex = this.playgroundTableColumns.indexOf(col) - 1;
      const featureData = this.invokeInputs[this.selectedFeature];
      const is2D = Array.isArray(featureData[0]);
      if (is2D) {
        featureData[rowIndex][colIndex] = value;
      } else {
        featureData[colIndex] = value;
      }
    }
    this.editingCell = null;
  }

  isEditing(element: any, col: string): boolean {
    return this.editingCell?.row === element['#'] && this.editingCell?.col === col;
  }

  resetInputs(): void {
    this.invokeResult = null;
    this.invokeInputs = JSON.parse(JSON.stringify(this.initialInputs));
    for (const f of this.invokeFeatures) {
      f.values = this.invokeInputs[f.name];
    }
    if (this.selectedFeature) {
      this.selectFeature(this.selectedFeature);
    }
  }

  private createZeros(shape: number[]): any {
    if (shape.length === 0) return 0;
    if (shape.length === 1) return new Array(shape[0]).fill(0);
    return new Array(shape[0]).fill(null).map(() => this.createZeros(shape.slice(1)));
  }
}
